import React, { useEffect, useRef } from 'react';

/**
 * A figure that keeps counting — the live rent and value numbers on Home.
 *
 * `at(ms)` returns the figure for a moment in time, so the number is always
 * computed from the clock rather than incremented: switching tabs, a sleeping
 * phone or a dropped frame can never let it drift from what it should say.
 *
 * Writes the text straight into the node instead of through React state, so
 * ten updates a second do not re-render the Home screen. Under
 * prefers-reduced-motion it updates once a second.
 */
interface Props {
  at: (ms: number) => number;
  locale: string;
  decimals?: number;
  className?: string;
}

const LiveTicker: React.FC<Props> = ({ at, locale, decimals = 2, className }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const fmt = useRef<Intl.NumberFormat | undefined>(undefined);
  fmt.current = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  useEffect(() => {
    let reduced = false;
    try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { /* old browser */ }
    const paint = () => {
      if (ref.current && fmt.current) ref.current.textContent = fmt.current.format(at(Date.now()));
    };
    paint();
    const id = window.setInterval(paint, reduced ? 1000 : 100);
    return () => window.clearInterval(id);
  }, [at]);

  return <span ref={ref} className={className} />;
};

export default LiveTicker;
